'use client';

import { useMemo } from 'react';
import type { CalendarEvent } from '@/lib/types';
import { getEventColor } from '@/lib/constants';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface LedgerHeroPeekProps {
  events: CalendarEvent[];
  year: number;
}

export function LedgerHeroPeek({ events, year }: LedgerHeroPeekProps) {
  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const future = events.filter((e) => e.dateEnd >= today);
    const list = future.length >= 8 ? future : events;
    return list
      .slice()
      .sort((a, b) => a.dateStart.localeCompare(b.dateStart))
      .slice(0, 8);
  }, [events]);

  return (
    <div className="animate-fade-in-up delay-700 mt-10 w-full max-w-md mx-auto">
      <div className="overflow-hidden content-card">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-text-primary">
            Upcoming Events
          </span>
          <span className="text-xs tabular-nums text-muted">
            {events.length} total
          </span>
        </div>

        {/* Event rows */}
        <div className="divide-y divide-border-subtle">
          {upcoming.map((evt) => {
            const d = new Date(evt.dateStart + 'T00:00:00');
            const month = MONTH_ABBR[d.getMonth()];
            const day = d.getDate();
            return (
              <div
                key={evt.id}
                className="flex items-center gap-3 px-4 py-2 text-sm"
              >
                {/* Date chip */}
                <span className="w-16 shrink-0 tabular-nums text-muted">
                  <span className="inline-block w-8">{month}</span>
                  <span className="text-text-secondary">{day}</span>
                </span>

                {/* Title */}
                <span className="min-w-0 flex-1 truncate text-text-primary">
                  {evt.title}
                </span>

                {/* Color dot */}
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: getEventColor(evt.type) }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
